import type { AssetMetadata, ColorLabel, RawRecipe } from "@focalfolio/domain-model";
import type { FileReference, WorkspaceFileSystem } from "@focalfolio/fs-contract";
import { parentPathOf } from "@focalfolio/domain-model";

export interface XmpUpdate {
  rating?: number;
  label?: ColorLabel;
  metadata?: Partial<AssetMetadata>;
  adjustment?: RawRecipe;
}

const XML_ESCAPE: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&apos;"
};

function escapeXml(value: string): string {
  return value.replace(/[&<>"']/gu, (character) => XML_ESCAPE[character] ?? character);
}

function decodeXml(value: string): string {
  return value
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&quot;", '"')
    .replaceAll("&apos;", "'")
    .replaceAll("&amp;", "&");
}

function arrayXml(namespace: string, key: string, values: string[]): string {
  if (values.length === 0) return "";
  return `<${namespace}:${key}><rdf:Bag>${values.map((value) => `<rdf:li>${escapeXml(value)}</rdf:li>`).join("")}</rdf:Bag></${namespace}:${key}>`;
}

function languageAltXml(namespace: string, key: string, value?: string): string {
  if (!value) return "";
  return `<${namespace}:${key}><rdf:Alt><rdf:li xml:lang="x-default">${escapeXml(value)}</rdf:li></rdf:Alt></${namespace}:${key}>`;
}

export function serializeXmp(update: XmpUpdate, preservedPacket?: string): string {
  const metadata = update.metadata ?? {};
  const preserved = preservedPacket ? `\n    <focalfolio:PreservedPacket><![CDATA[${preservedPacket.replaceAll("]]>", "]]]]><![CDATA[>")}]]></focalfolio:PreservedPacket>` : "";
  const adjustment = update.adjustment
    ? `<focalfolio:Adjustments>${escapeXml(JSON.stringify(update.adjustment))}</focalfolio:Adjustments>`
    : "";
  return `<?xpacket begin="\uFEFF" id="W5M0MpCehiHzreSzNTczkc9d"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/" x:xmptk="FocalFolio 0.2.0">
  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
    <rdf:Description rdf:about=""
      xmlns:xmp="http://ns.adobe.com/xap/1.0/"
      xmlns:dc="http://purl.org/dc/elements/1.1/"
      xmlns:xmpRights="http://ns.adobe.com/xap/1.0/rights/"
      xmlns:focalfolio="https://focalfolio.com/ns/1.0/"
      ${update.rating !== undefined ? `xmp:Rating="${Math.max(0, Math.min(5, Math.round(update.rating)))}"` : ""}
      ${update.label ? `xmp:Label="${escapeXml(update.label)}"` : ""}>
      ${arrayXml("dc", "subject", metadata.keywords ?? [])}
      ${arrayXml("dc", "creator", metadata.creator ? [metadata.creator] : [])}
      ${languageAltXml("dc", "title", metadata.title)}
      ${languageAltXml("dc", "description", metadata.description)}
      ${languageAltXml("dc", "rights", metadata.copyright)}
      ${languageAltXml("xmpRights", "UsageTerms", metadata.usageTerms)}
      ${adjustment}${preserved}
    </rdf:Description>
  </rdf:RDF>
</x:xmpmeta>
<?xpacket end="w"?>`;
}

function attribute(packet: string, name: string): string | undefined {
  const match = packet.match(new RegExp(`${name}=["']([^"']*)["']`, "u"));
  return match?.[1] ? decodeXml(match[1]) : undefined;
}

function element(packet: string, qualifiedName: string): string | undefined {
  const escaped = qualifiedName.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
  const alt = packet.match(new RegExp(`<${escaped}>[\\s\\S]*?<rdf:li[^>]*>([\\s\\S]*?)<\\/rdf:li>[\\s\\S]*?<\\/${escaped}>`, "u"));
  if (alt?.[1]) return decodeXml(alt[1].trim());
  const plain = packet.match(new RegExp(`<${escaped}[^>]*>([\\s\\S]*?)<\\/${escaped}>`, "u"));
  return plain?.[1] ? decodeXml(plain[1].trim()) : undefined;
}

function bag(packet: string, qualifiedName: string): string[] {
  const escaped = qualifiedName.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
  const container = packet.match(new RegExp(`<${escaped}>[\\s\\S]*?<rdf:Bag>([\\s\\S]*?)<\\/rdf:Bag>[\\s\\S]*?<\\/${escaped}>`, "u"));
  if (!container?.[1]) return [];
  return [...container[1].matchAll(/<rdf:li[^>]*>([\s\S]*?)<\/rdf:li>/gu)].map((match) => decodeXml((match[1] ?? "").trim()));
}

export function parseXmp(packet: string): XmpUpdate {
  const ratingText = attribute(packet, "xmp:Rating");
  const adjustmentText = element(packet, "focalfolio:Adjustments");
  let adjustment: RawRecipe | undefined;
  if (adjustmentText) {
    try {
      adjustment = JSON.parse(adjustmentText) as RawRecipe;
    } catch {
      adjustment = undefined;
    }
  }
  return {
    ...(ratingText ? { rating: Number.parseInt(ratingText, 10) } : {}),
    ...(attribute(packet, "xmp:Label") ? { label: attribute(packet, "xmp:Label") as ColorLabel } : {}),
    metadata: {
      keywords: bag(packet, "dc:subject"),
      title: element(packet, "dc:title"),
      description: element(packet, "dc:description"),
      creator: bag(packet, "dc:creator")[0],
      copyright: element(packet, "dc:rights"),
      usageTerms: element(packet, "xmpRights:UsageTerms")
    },
    ...(adjustment ? { adjustment } : {})
  };
}

export function sidecarPathFor(relativePath: string): string {
  const index = relativePath.lastIndexOf(".");
  return `${index > relativePath.lastIndexOf("/") ? relativePath.slice(0, index) : relativePath}.xmp`;
}

export async function writeXmpSidecarSafely(
  fileSystem: WorkspaceFileSystem,
  source: FileReference,
  update: XmpUpdate
): Promise<{ sidecar: FileReference; backup?: FileReference }> {
  const sidecar: FileReference = { rootId: source.rootId, relativePath: sidecarPathFor(source.relativePath) };
  const temporary: FileReference = { rootId: source.rootId, relativePath: `${sidecar.relativePath}.focalfolio-${crypto.randomUUID()}.tmp` };
  const backup: FileReference = {
    rootId: source.rootId,
    relativePath: `${parentPathOf(sidecar.relativePath) ? `${parentPathOf(sidecar.relativePath)}/` : ""}.focalfolio-backups/${sidecar.relativePath.split("/").at(-1)}.${Date.now()}.bak`
  };

  let existing: string | undefined;
  try {
    existing = await (await fileSystem.getFile(sidecar)).text();
  } catch {
    existing = undefined;
  }
  const packet = serializeXmp(update, existing);
  const bytes = new TextEncoder().encode(packet);
  await fileSystem.writeFile(temporary, bytes, { overwrite: false });
  const verified = parseXmp(await (await fileSystem.getFile(temporary)).text());
  if (update.rating !== undefined && verified.rating !== Math.max(0, Math.min(5, Math.round(update.rating)))) {
    await fileSystem.removePermanently(temporary).catch(() => undefined);
    throw new Error("XMP verification failed before commit.");
  }

  let backupCreated = false;
  if (existing !== undefined) {
    await fileSystem.copyVerified(sidecar, backup, { digest: true });
    backupCreated = true;
  }
  try {
    await fileSystem.writeFile(sidecar, bytes, { overwrite: true });
    parseXmp(await (await fileSystem.getFile(sidecar)).text());
  } catch (error) {
    if (backupCreated) {
      await fileSystem.copyVerified(backup, sidecar, { overwrite: true, digest: true }).catch(() => undefined);
    }
    throw error;
  } finally {
    await fileSystem.removePermanently(temporary).catch(() => undefined);
  }
  return { sidecar, ...(backupCreated ? { backup } : {}) };
}
